import { DollarSign, ShoppingBag, MousePointerClick, Eye, Globe, XCircle, FileText, TrendingUp, Clock, RefreshCw, AlertTriangle, HelpCircle } from "lucide-react";

const Kpi = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="bg-[#0E162B]/80 border border-[#C9A66A]/25 rounded-2xl p-6" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs tracking-[0.2em] text-[#A7956E]">{label}</span>
      <Icon className="w-5 h-5 text-[#C9A66A]" strokeWidth={1.5} />
    </div>
    <div className="text-3xl sm:text-4xl font-black text-white tabular-nums">{value}</div>
    {sub && <div className="text-xs text-[#A7956E] mt-2">{sub}</div>}
  </div>
);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export const CoursePanel = ({ stats, onReconcile, reconciling, reconcileResult }) => {
  const s = stats || {};
  return (
    <div className="max-w-7xl mx-auto space-y-8" dir="ltr" data-testid="course-panel">
      {/* Reconcile bar */}
      <div className="bg-[#0E162B]/80 border border-[#C9A66A]/30 rounded-2xl p-5 flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between" data-testid="course-reconcile-bar">
        <div className="flex-1">
          <div className="text-[11px] tracking-[0.22em] text-[#C9A66A] font-bold mb-1">PAYMENT RECONCILIATION</div>
          <div className="text-[13px] text-[#A7956E] leading-relaxed">
            Asks Stripe directly about every unpaid order — recovers anything where the webhook never arrived and surfaces decline reasons for the rest.
          </div>
          {reconcileResult && (
            <div className="mt-2 text-[12.5px] text-[#E7C98C]" data-testid="course-reconcile-result">
              Checked <span className="font-bold">{reconcileResult.summary?.checked || 0}</span> · Fixed <span className="font-bold text-white">{reconcileResult.summary?.fixed || 0}</span> · Failed <span className="font-bold">{reconcileResult.summary?.failed || 0}</span> · Expired <span className="font-bold">{reconcileResult.summary?.expired || 0}</span> · Still open <span className="font-bold">{reconcileResult.summary?.still_open || 0}</span>
            </div>
          )}
        </div>
        <button
          onClick={onReconcile}
          disabled={reconciling}
          data-testid="course-reconcile-btn"
          className="bg-gradient-to-b from-[#E2C281] to-[#C9A66A] hover:from-[#EBCD93] hover:to-[#D6B477] text-[#0A0F1C] font-extrabold text-[13px] tracking-wide px-5 py-3 rounded-full transition-all flex items-center justify-center gap-2 min-h-[44px] disabled:opacity-60 shadow-[0_10px_30px_-12px_rgba(201,166,106,0.6)] shrink-0"
        >
          <RefreshCw className={`w-4 h-4 ${reconciling ? "animate-spin" : ""}`} />
          {reconciling ? "Revising with Stripe…" : "Revise payments with Stripe"}
        </button>
      </div>

      {reconcileResult?.decline_codes?.length > 0 && (
        <div className="bg-[#0E162B]/80 border border-[#F87171]/25 rounded-2xl p-5" data-testid="course-decline-codes">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-[#FCA5A5]" />
            <h3 className="text-[13px] font-bold tracking-wider text-white">DECLINE CODES (last reconcile)</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {reconcileResult.decline_codes.map(([code, count]) => (
              <span key={code} className="text-[12px] bg-[#F87171]/[0.1] border border-[#F87171]/30 text-[#FCA5A5] px-2.5 py-1 rounded-sm">
                <span className="font-mono">{code}</span> <span className="font-bold text-white">×{count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Revenue / orders KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        <Kpi icon={DollarSign} label="REVENUE" value={`${(s.revenue || 0).toFixed(0)} ${s.currency || "SAR"}`} sub={`${s.orders_paid || 0} paid · ${(s.price || 99).toFixed(0)} ${s.currency || "SAR"} each`} testid="course-stat-revenue" />
        <Kpi icon={ShoppingBag} label="ORDERS" value={`${s.orders_paid || 0} / ${s.orders_total || 0}`} sub={`${s.checkout_conversion_pct || 0}% checkout conversion`} testid="course-stat-orders" />
        <Kpi icon={Eye} label="PAGE VISITS" value={s.visits || 0} sub="landing page views" testid="course-stat-visits" />
        <Kpi icon={TrendingUp} label="VISIT → PURCHASE" value={`${s.purchase_conversion_pct || 0}%`} sub={`${s.orders_paid || 0} paid / ${s.visits || 0} visits`} testid="course-stat-conversion" />
      </div>

      {/* Funnel KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        <Kpi icon={MousePointerClick} label="CTA CLICKS" value={s.cta_clicks || 0} sub={`${s.ctr_pct || 0}% of visits`} testid="course-stat-cta" />
        <Kpi icon={HelpCircle} label="FAQ OPENS" value={s.faq_opens || 0} sub="engagement signal" testid="course-stat-faq" />
        <Kpi icon={Clock} label="AVG TIME ON SITE" value={`${Math.floor((s.avg_time_on_site_sec || 0) / 60)}m ${(s.avg_time_on_site_sec || 0) % 60}s`} sub="per landing visit" testid="course-stat-tos" />
        <Kpi icon={FileText} label="PRICE" value={`${(s.price || 99).toFixed(0)} ${s.currency || "SAR"}`} sub="one-time · MVP launch" testid="course-stat-price" />
      </div>

      {/* Countries */}
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-[#0E162B]/80 border border-[#C9A66A]/25 rounded-2xl p-6" data-testid="course-payment-countries">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-4 h-4 text-[#C9A66A]" />
            <h2 className="text-sm font-bold tracking-wider text-white">PAYMENTS BY COUNTRY</h2>
          </div>
          {(s.payment_countries || []).length === 0 ? (
            <div className="text-sm text-[#A7956E]">No payments yet.</div>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
              {(s.payment_countries || []).map((c) => (
                <div key={c.country} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                  <span className="text-white font-bold">{c.country || "Unknown"}</span>
                  <span className="text-[#A7956E]">{c.count} payment{c.count > 1 ? "s" : ""} · <span className="text-[#C9A66A] font-bold">{(c.revenue || 0).toFixed(0)} {s.currency || "SAR"}</span></span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-[#0E162B]/80 border border-[#C9A66A]/25 rounded-2xl p-6" data-testid="course-visit-countries">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-4 h-4 text-[#E7C98C]" />
            <h2 className="text-sm font-bold tracking-wider text-white">VISITS BY COUNTRY</h2>
          </div>
          {(s.visit_countries || []).length === 0 ? (
            <div className="text-sm text-[#A7956E]">No tracked visits yet.</div>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
              {(s.visit_countries || []).map((c) => (
                <div key={c.country} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                  <span className="text-white font-bold">{c.country}</span>
                  <span className="text-[#A7956E]">{c.count} visit{c.count > 1 ? "s" : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Platform micro-survey breakdown */}
      <div className="grid lg:grid-cols-2 gap-5" data-testid="course-platform-breakdown">
        <div className="bg-[#0E162B]/80 border border-[#C9A66A]/25 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-[#C9A66A]" />
            <h2 className="text-sm font-bold tracking-wider text-white">PLATFORM — PAID BUYERS</h2>
          </div>
          {(s.platform_paid || []).length === 0 ? (
            <div className="text-sm text-[#A7956E]">No paid orders yet.</div>
          ) : (
            <div className="space-y-2">
              {(s.platform_paid || []).map((p) => (
                <div key={p.platform} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                  <span className="text-white font-bold capitalize">{p.platform === "not_started" ? "Not started yet" : p.platform}</span>
                  <span className="text-[#C9A66A] font-bold">{p.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-[#0E162B]/80 border border-[#C9A66A]/25 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <MousePointerClick className="w-4 h-4 text-[#E7C98C]" />
            <h2 className="text-sm font-bold tracking-wider text-white">PLATFORM — ALL CHECKOUT ATTEMPTS</h2>
          </div>
          {(s.platform_attempts || []).length === 0 ? (
            <div className="text-sm text-[#A7956E]">No attempts yet.</div>
          ) : (
            <div className="space-y-2">
              {(s.platform_attempts || []).map((p) => (
                <div key={p.platform} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                  <span className="text-white font-bold capitalize">{p.platform === "not_started" ? "Not started yet" : p.platform}</span>
                  <span className="text-[#A7956E]">{p.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Unpaid attempts */}
      <div className="bg-[#0E162B]/80 border border-[#C9A66A]/25 rounded-2xl p-6" data-testid="course-unpaid-attempts">
        <div className="flex items-center gap-2 mb-4">
          <XCircle className="w-4 h-4 text-[#F87171]" />
          <h2 className="text-sm font-bold tracking-wider text-white">PAYMENTS NOT PROCESSED (LAST {(s.unpaid_attempts || []).length})</h2>
        </div>
        {(s.unpaid_attempts || []).length === 0 ? (
          <div className="text-sm text-[#A7956E]">No unpaid checkout attempts.</div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="text-[11px] tracking-wider text-[#A7956E] border-b border-white/10">
                <th className="py-2 pr-3">WHEN</th>
                <th className="py-2 pr-3">EMAIL</th>
                <th className="py-2 pr-3">COUNTRY</th>
                <th className="py-2 pr-3">STATUS</th>
                <th className="py-2">REASON</th>
              </tr>
            </thead>
            <tbody>
              {(s.unpaid_attempts || []).map((a, i) => (
                <tr key={i} className="border-b border-white/5 align-top">
                  <td className="py-2.5 pr-3 text-[#A7956E] whitespace-nowrap">{fmtDate(a.created_at)}</td>
                  <td className="py-2.5 pr-3 text-white">{a.email || "—"}</td>
                  <td className="py-2.5 pr-3 text-white">{a.country || "—"}</td>
                  <td className="py-2.5 pr-3">
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                      a.stripe_status === "expired" || a.status === "expired"
                        ? "bg-[#F87171]/15 text-[#FCA5A5]"
                        : "bg-[#FBBF24]/15 text-[#FDE68A]"
                    }`}>{a.stripe_status || a.status || "created"}</span>
                  </td>
                  <td className="py-2.5 text-[#A7956E] text-[12px]">{a.reason || "Not checked yet"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Recent paid orders */}
      <div className="bg-[#0E162B]/80 border border-[#C9A66A]/25 rounded-2xl p-6" data-testid="course-recent-orders">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-4 h-4 text-[#C9A66A]" />
          <h2 className="text-sm font-bold tracking-wider text-white">RECENT PAID ORDERS ({(s.recent_orders || []).length})</h2>
        </div>
        {(s.recent_orders || []).length === 0 ? (
          <div className="text-sm text-[#A7956E]">No paid orders yet.</div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="text-[11px] tracking-wider text-[#A7956E] border-b border-white/10">
                <th className="py-2 pr-3">WHEN</th>
                <th className="py-2 pr-3">EMAIL</th>
                <th className="py-2 pr-3">COUNTRY</th>
                <th className="py-2">AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              {(s.recent_orders || []).map((o, i) => (
                <tr key={i} className="border-b border-white/5">
                  <td className="py-2.5 pr-3 text-[#A7956E] whitespace-nowrap">{fmtDate(o.paid_at)}</td>
                  <td className="py-2.5 pr-3 text-white">{o.email || "—"}</td>
                  <td className="py-2.5 pr-3 text-white">{o.country || "—"}</td>
                  <td className="py-2.5 text-[#C9A66A] font-bold">{((o.amount_paid || 0) / 100).toFixed(0)} {(o.currency || "SAR").toUpperCase()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="text-[11px] text-[#A7956E] flex items-center gap-2">
        <FileText className="w-3.5 h-3.5" />
        Performance Ads OS · replyrescue.app/perfads · 99 SAR · Stripe Checkout (LIVE)
      </div>
    </div>
  );
};
